import { useCallback, useEffect, useRef, useState } from 'react';

import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { focusFieldControl } from 'src/utils/focus-field-control';

/**
 * A required field opts into footer navigation by rendering `data-required-field`
 * while it is still unanswered, so the footer can walk the remaining required
 * ones in visual order without every section reporting its values up to the page.
 */
const REQUIRED_FIELD_SELECTOR = '[data-required-field]';

/** Where the search starts from before the reader has jumped anywhere. */
const VIEWPORT_ANCHOR_RATIO = 0.25;

interface RequiredFieldNavigation {
  jumpToNextRequiredField: () => void;
  jumpToPreviousRequiredField: () => void;
  requiredFieldCount: number;
  setContainerEl: (element: HTMLElement | null) => void;
}

/** Cycles the reader through required fields that still need an answer. */
export function useRequiredFieldNavigation(): RequiredFieldNavigation {
  const lastJumpedField = useRef<HTMLElement | null>(null);
  const [requiredFieldCount, setRequiredFieldCount] = useState(0);
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  /**
   * Fields mark themselves in the DOM as they are answered, so watch for that
   * rather than asking every section to report its values up to the footer.
   */
  useEffect(() => {
    if (!containerEl) return;

    const recount = () => setRequiredFieldCount(getRequiredFields(containerEl).length);
    recount();

    const observer = new MutationObserver(recount);
    observer.observe(containerEl, {
      attributeFilter: ['data-required-field'],
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [containerEl]);

  const jumpToRequiredField = useCallback(
    (direction: 'next' | 'back') => {
      const fields = getRequiredFields(containerEl);
      const target = getFieldInDirection({
        anchorTop: getAnchorTop(lastJumpedField.current),
        direction,
        fields,
      });
      if (!target) return;

      lastJumpedField.current = target;
      scrollIntoView(target, { behavior: 'smooth', block: 'center' });
      focusFieldControl(target);
    },
    [containerEl]
  );

  return {
    jumpToNextRequiredField: useCallback(() => jumpToRequiredField('next'), [jumpToRequiredField]),
    jumpToPreviousRequiredField: useCallback(
      () => jumpToRequiredField('back'),
      [jumpToRequiredField]
    ),
    requiredFieldCount,
    setContainerEl,
  };
}

function getRequiredFields(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return [...container.querySelectorAll<HTMLElement>(REQUIRED_FIELD_SELECTOR)];
}

/**
 * Measure from the field we last jumped to. It stays a valid anchor after the
 * reader answers it and it drops out of the list, so the next jump continues from
 * where they were working rather than restarting at the top of the page.
 */
function getAnchorTop(lastJumpedField: HTMLElement | null): number {
  if (lastJumpedField?.isConnected) return lastJumpedField.getBoundingClientRect().top;
  return window.innerHeight * VIEWPORT_ANCHOR_RATIO;
}

/** Wraps around at either end so the chevrons never dead-end on a full page. */
function getFieldInDirection({
  anchorTop,
  direction,
  fields,
}: {
  anchorTop: number;
  direction: 'next' | 'back';
  fields: HTMLElement[];
}): HTMLElement | null {
  if (fields.length === 0) return null;

  switch (direction) {
    case 'next': {
      const below = fields.find(field => field.getBoundingClientRect().top > anchorTop + 1);
      return below ?? fields[0];
    }
    case 'back': {
      const above = [...fields]
        .reverse()
        .find(field => field.getBoundingClientRect().top < anchorTop - 1);
      return above ?? fields[fields.length - 1];
    }
  }
}
